// Who may see and change what on one staff member — the Roster's ⋯ menu and the
// staff detail modal (BL-39/40) both read this, so the two cannot drift apart.
//
// It invents no authority. Every edit gate is the one the Roster already had,
// moved here from RosterPage/RosterRowActions; the database still decides
// (RLS on `staff`, the rank Edge routes, `set_rider_vehicle`). This is UI gating.
//
// ⚠ MONEY IS ADMIN AND SUPERADMIN ONLY. A hub manager is never handed a
// per-staff money aggregate (0130's reasoning, intake 2026-09-26), so the modal
// is built role-aware rather than hidden after the fact: no totals, no booking
// amounts, no commission rate. Executive is not given it either — the
// brief names admin and superadmin, and widening it would be a new decision.

import type { Enums } from "@bednbody/types/database";
import type { StaffRole } from "@/lib/jwt-claims";

export interface StaffAccess {
  /** Totals, booking amounts and the commission rate. */
  canSeeMoney: boolean;
  canEditName: boolean;
  canToggleActive: boolean;
  canChangeRole: boolean;
  canReassignHub: boolean;
  canSetRiderRank: boolean;
  canSetTherapistRank: boolean;
  canSetPlate: boolean;
  /** 0311 — the six service switches on a therapist's profile. */
  canSeeServices: boolean;
  canSetServices: boolean;
  /** Staff password reset fallback (Leon, 2026-10-17): Admin and Superadmin. */
  canResetPassword: boolean;
}

export function staffDetailAccess(
  viewer: StaffRole | null,
  person: { role: Enums<"user_role"> },
): StaffAccess {
  const isHubManager = viewer === "hub_manager";
  // HR is out of scope (Leon, 2026-09-17 and 2026-10-04): admin and superadmin
  // manage staff; an `hr` login gets the read-only view, like executive.
  const canManage = viewer === "admin" || viewer === "superadmin";
  // Nobody below superadmin may be handed a superadmin's identity or role —
  // the Roster never offered it, and who may mint one is undefined.
  const targetIsEditable = person.role !== "superadmin";

  return {
    canSeeMoney: viewer === "admin" || viewer === "superadmin",
    // A plain `staff_admin_write` update, the same grant role and hub use.
    canEditName: canManage && targetIsEditable,
    // `staff_hub_toggle` (0003) lets a hub manager flip `active` on their own hub.
    canToggleActive: isHubManager || canManage,
    canChangeRole: canManage && targetIsEditable,
    canReassignHub: canManage,
    // RIDER-031 names an authorized Admin for rider rank.
    canSetRiderRank: canManage && person.role === "rider",
    // Nothing names anyone for therapist rank (OQ-80); 0074 and the Edge route
    // allow superadmin alone.
    canSetTherapistRank: viewer === "superadmin" && person.role === "therapist",
    // 0279: the rider's hub manager, or an admin.
    canSetPlate: (isHubManager || canManage) && person.role === "rider",
    // 0311: switches are qualifications, so the authority is today's
    // grant_service_qualification — superadmin (0037). Admin sees them.
    canSeeServices: canManage && person.role === "therapist",
    canSetServices: viewer === "superadmin" && person.role === "therapist",
    // DECISIONS 2026-10-17: Admin and Superadmin, never a hub manager. Partner-app
    // accounts only; the admin Edge route refuses any other role.
    canResetPassword: canManage && (person.role === "therapist" || person.role === "rider"),
  };
}

/** Staff whose work is bookings — the others have no booking history to show. */
export function hasBookingHistory(role: Enums<"user_role">): role is "therapist" | "rider" {
  return role === "therapist" || role === "rider";
}
